import { type ReactElement, type ReactNode, useCallback, useMemo } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import { resolveSemanticColor } from '@/theme/semanticTokens';
import { StatusBanner } from '@/ui';
import { Box } from '@/ui/primitives/Box';
import { NotificationContext, type NotificationContextValue } from './notificationContext';
import type { UiTone } from '@/theme/uiTokens';

export const NotificationProvider = ({ children }: { children: ReactNode }): ReactElement => {
  const notify = useCallback(
    (toastMessage: { message: ReactNode; tone?: UiTone; timeoutMs?: number }) => {
      const { message, tone = 'blue', timeoutMs } = toastMessage;
      const isErrorToast = tone === 'red';
      const resolvedTimeoutMs =
        typeof timeoutMs === 'number' && timeoutMs > 0 ? timeoutMs : isErrorToast ? false : 4000;
      const toastId = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const bannerContent = message ? (
        <Box
          className="flowqa-toast-message"
          onPointerDownCapture={(event) => {
            event.stopPropagation();
          }}
          onPointerMoveCapture={(event) => {
            event.stopPropagation();
          }}
          onPointerUpCapture={(event) => {
            event.stopPropagation();
          }}
          onMouseDownCapture={(event) => {
            event.stopPropagation();
          }}
          onMouseMoveCapture={(event) => {
            event.stopPropagation();
          }}
          onMouseUpCapture={(event) => {
            event.stopPropagation();
          }}
          sx={{ userSelect: 'text', WebkitUserSelect: 'text', cursor: 'text' }}
        >
          {typeof message === 'string' ? message : <>{message}</>}
        </Box>
      ) : undefined;
      toast(
        () => (
          <Box
            sx={{
              '& .MuiAlert-message, & .MuiAlert-message *': {
                userSelect: 'text',
                WebkitUserSelect: 'text',
                cursor: 'text',
              },
              '& .MuiAlert-icon, & .MuiAlert-action, & .MuiButton-root, & .MuiIconButton-root': {
                userSelect: 'none',
                WebkitUserSelect: 'none',
                cursor: 'pointer',
              },
            }}
          >
            <StatusBanner
              tone={tone}
              dense
              onClose={() => {
                toast.dismiss(toastId);
              }}
              sx={(theme) => ({
                backgroundColor: resolveSemanticColor(theme, 'pageBg'),
                userSelect: 'text',
                WebkitUserSelect: 'text',
                '& .MuiAlert-icon': {
                  marginRight: '4px',
                },
                '& .MuiAlert-message': {
                  userSelect: 'text',
                  WebkitUserSelect: 'text',
                },
              })}
            >
              {bannerContent}
            </StatusBanner>
          </Box>
        ),
        {
          autoClose: resolvedTimeoutMs,
          toastId,
          className: 'flowqa-toast-selectable',
          style: {
            userSelect: 'text',
            WebkitUserSelect: 'text',
            touchAction: 'auto',
            cursor: 'default',
          },
          closeButton: false,
          closeOnClick: false,
          icon: false,
          pauseOnFocusLoss: false,
          pauseOnHover: false,
          draggable: false,
        },
      );
      if (typeof resolvedTimeoutMs === 'number') {
        window.setTimeout(() => {
          toast.dismiss(toastId);
        }, resolvedTimeoutMs);
      }
    },
    [],
  );

  const value = useMemo<NotificationContextValue>(() => ({ notify }), [notify]);
  return (
    <NotificationContext.Provider value={value}>
      {children}
      <ToastContainer
        position="bottom-right"
        closeButton={false}
        closeOnClick={false}
        newestOnTop
        autoClose={4000}
        pauseOnFocusLoss={false}
        pauseOnHover={false}
        draggable={false}
      />
    </NotificationContext.Provider>
  );
};
